import * as React from 'react';
import { useMemo } from 'react';
import { cn } from '../../lib/utils';
import { formatPhoneNumber } from './util';
import { countries } from './countries';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { CountryItem, PhoneInputProps } from './types';
import useI18n from '../../hooks/useI18n';
import {
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxTriggerButton,
  ComboboxContent,
  ComboboxInput,
  ComboboxList,
  ComboboxCollection,
  ComboboxItem,
  ComboboxEmpty,
  ComboboxLabel,
  useComboboxAnchor,
} from '../Combobox';

export const PhoneInput = React.forwardRef<HTMLDivElement, PhoneInputProps>(
  (
    {
      inputProps,
      popupContainer,
      defaultValue,
      onChange,
      onSelect,
      value,
      error = false,
      label,
      ariaLabelNoCountry,
      disabled = false,
      required = false,
      className,
      ...props
    },
    ref,
  ) => {
    const { t } = useI18n();
    const anchor = useComboboxAnchor();
    const generatedId = React.useId();
    const triggerId = generatedId;
    const labelId = `${triggerId}-label`;

    // Sort items by length of value to ensure that the longest matches are selected first
    const sortedItems = useMemo(
      () => [...countries].sort((a, b) => b.value.length - a.value.length),
      [],
    );

    const [selected, setSelected] = React.useState<CountryItem | null>(null);
    const [internalValue, setInternalValue] = React.useState<
      string | undefined
    >(defaultValue ? formatPhoneNumber(defaultValue, sortedItems) : undefined);
    const isControlled = value !== undefined;
    const displayValue = isControlled
      ? formatPhoneNumber(value, sortedItems)
      : internalValue;

    React.useEffect(() => {
      if (!displayValue) {
        setSelected(null);
        return;
      }

      const term = displayValue.replace(/ /g, '');
      if (selected && term.startsWith(selected.value)) {
        return;
      }

      const item =
        sortedItems.find((sortedItem) => term.startsWith(sortedItem.value)) ||
        null;
      setSelected(item);
    }, [displayValue, selected, sortedItems]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const phone = formatPhoneNumber(e.target.value, sortedItems);
      if (!isControlled) {
        setInternalValue(phone);
      }
      onChange?.(e);
    };

    const handleSelect = (selectedValue: CountryItem | null) => {
      if (!selectedValue) return;
      const phoneCode = selectedValue.value;

      if (!isControlled) {
        setInternalValue(phoneCode);
      }
      phoneRef.current?.focus();

      setSelected(selectedValue);
      onSelect?.(phoneCode);
      onChange?.({
        target: { value: phoneCode },
      } as React.ChangeEvent<HTMLInputElement>);
    };

    const phoneRef = React.useRef<HTMLInputElement>(null);

    const {
      className: inputClassName,
      onClick,
      onMouseDown,
      ...restInputProps
    } = inputProps ?? {};

    return (
      <ComboboxRoot<CountryItem>
        disabled={disabled}
        items={countries}
        value={selected}
        onValueChange={handleSelect}
        itemToStringLabel={(item) => item.label}
        itemToStringValue={(item) => `${item.value}|${item.iso2}`}
        autoHighlight
      >
        <div
          ref={ref}
          data-slot='phone-input'
          className={cn(
            'eye:group/combobox-field eye:block eye:w-full',
            className,
          )}
        >
          <ComboboxTrigger
            ref={anchor}
            disabled={disabled}
            aria-invalid={error || undefined}
          >
            <ComboboxLabel id={labelId} htmlFor={triggerId} placement='inside'>
              {required ? `${label} *` : label}
            </ComboboxLabel>
            <ComboboxTriggerButton
              id={triggerId}
              aria-invalid={error || undefined}
              aria-labelledby={labelId}
            >
              <span
                data-slot='phone-input-flag'
                className='eye:flex eye:shrink-0 eye:items-center'
              >
                {selected?.iso2 ? (
                  <img
                    className='eye:border eye:border-grey-100'
                    alt={selected.label}
                    src={`https://flagcdn.com/h20/${selected.iso2}.png`}
                    height='20'
                    width='20'
                    loading='lazy'
                  />
                ) : (
                  <IconAlertTriangle
                    aria-label={ariaLabelNoCountry ?? t('noCountrySelected')}
                    size={20}
                    className='eye:text-red-100'
                  />
                )}
              </span>
              <input
                data-slot='phone-input-control'
                maxLength={20}
                className={cn(
                  // Layout
                  'eye:h-full eye:w-full eye:min-w-0 eye:flex-1',
                  // Surface & typography
                  'eye:body-m eye:border-0 eye:bg-transparent eye:text-black',
                  // States
                  'eye:outline-none eye:focus:ring-0 eye:focus:outline-none',
                  'eye:disabled:cursor-not-allowed eye:disabled:text-grey-disabled',
                  inputClassName,
                )}
                {...restInputProps}
                {...props}
                required={required}
                disabled={disabled}
                ref={phoneRef}
                value={displayValue ?? ''}
                onChange={handleChange}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  onMouseDown?.(
                    e as unknown as React.MouseEvent<HTMLInputElement>,
                  );
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClick?.(e as unknown as React.MouseEvent<HTMLInputElement>);
                }}
              />
            </ComboboxTriggerButton>
          </ComboboxTrigger>
        </div>
        <ComboboxContent anchor={anchor} container={popupContainer}>
          <ComboboxInput />
          <ComboboxEmpty />
          <ComboboxList>
            <ComboboxCollection>
              {(item: CountryItem) => (
                <ComboboxItem key={`${item.value}|${item.iso2}`} value={item}>
                  <img
                    className='eye:border eye:border-grey-100'
                    alt={item.label}
                    src={`https://flagcdn.com/h20/${item.iso2}.png`}
                    height='20'
                    width='20'
                    loading='lazy'
                  />
                  {item.label}
                </ComboboxItem>
              )}
            </ComboboxCollection>
          </ComboboxList>
        </ComboboxContent>
      </ComboboxRoot>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';
